<template>
    <div>
        <v-card
                class="float-e-margins no-margin publish"
                elevation="0"
                style="overflow: hidden; overflow: clip;">
            <div class="elevation-6 publish-editor-header">
                <div class="publish-editor-controls-container" @click.self="activeElementIndex=-1">
                    <div id="select-active_dropdown"
                         @click.self="activeElementIndex=-1">
                        <div class="publish-select-layer-dropdown">
                            <publish-layer-select
                                :activeLayerIndex="activeLayerIndex"
                                :selectableLayers="layerSelectOptions"
                                @change="changeLayer()"
                                @input="val => activeLayerIndex = val"
                            />
                        </div>
                        <div class="publish-select-layer-buttons">
                            <preview-size-change-button
                                :line-size="lineSize"
                                @input="lineSize = $event"
                            />
                            <square-button
                                :icon="gridSizeIcon"
                                :tooltip-text="t_editor_layout"
                                flat
                                @click="$emit('change-grid', nextGridSize)"
                            />
                        </div>
                    </div>

                    <div style="display: flex; flex-wrap: wrap; justify-content: end; align-content: center; gap: 5px;" @click.self="activeElementIndex=-1">
                        <publish-reload-button
                            :disabled="!inner_layers.length"
                            @click="newActiveElementIndex = activeElementIndex; loadLayer()"
                        />
                        <publish-approve-all-button v-if="active_layer.elements.length > 0"
                            :elements="visibleElements"
                            @approve="() => approvalSystem.approveAll()"
                            @disapprove="() => approvalSystem.disapproveAll()"
                        />
                        <publish-preview-button
                            :disabled="!inner_layers.length"
                            :layer="active_layer"
                            :elements="visibleElements"
                            :screen-id="filter.screen"
                        />
                        <publish-publish-button
                            :disabled="!inner_layers.length || publishingDisabled"
                            :loading="publishing"
                            :color="publishButtonColor"
                            :dialogText="publishText + ' ' + publishScreenScreengroup"
                            @publish="publish"
                        />
                    </div>
                </div>
                <publish-filter
                    :preserve-date-filters="preserveDateFilters"
                    :screens-data="screensData"
                    @click="activeElementIndex = -1"
                    @input="onFiltersChanged"
                />
            </div>
            <div ref="listBox"
                 :style="{height: `${editorHeight}px`, paddingTop: '6px'}"
                 class="element-list scrollBox"
                 @click.self="activeElementIndex=-1"
                 @scroll.passive="handleEditorScroll">
                <transition name="fade"
                            appear>
                    <div v-if="isOverlayActive()"
                         :data-key="active_layer.id"
                         class="dragdrop-placeholder"
                         :style="placeholderPositionStyle">
                        <div class="wrapper font-bold bg-muted">
                            <transition name="fade"
                                        appear>
                                <loading-placeholder v-if="loading" />
                                <div v-else
                                     class="dragdrop-placeholder-text">
                                    {{ editorMessage }}
                                </div>
                            </transition>
                        </div>
                    </div>
                </transition>
                <div ref="listContent"
                     :style="{
                            '--thumbnail-height-ul': thumbnailHeight,
                            '--thumbnail-aspect-ratio': layerAspectRatio
                         }">
                    <transition name="fade"
                                appear>
                    <!-- The key of the component forces vue to rerender upon publishing (-> change of element state) -->
                    <draggable v-if="displayedElements.length > 0"
                               :value="visibleElements"
                               @input="handleDragChange"
                               :move="moveDrag"
                               handle=".publish-element"
                               @end="endDrag($event)"
                               @start="startDrag($event)">
                        <template v-for="(element, index) in visibleElements">
                            <div :key="index" class="publish-element-container"
                                 @click="handleElementClick"
                            >
                                <!-- 'element' here is not of the correct type, but good enough for the props accessed -->
                                <provider-publish-table
                                    :element="visibleElements[index]"
                                    :layer="tableLayer">
                                    <publish-element
                                        v-model="activeElementIndex"
                                        :active_layer="active_layer"
                                        :approve-privilege="approvePrivilege"
                                        :aspect-ratio="layerAspectRatio"
                                        :delete-privilege="deletePrivilege"
                                        :edit-privilege="editPrivilege"
                                        :element="element"
                                        :element-height="thumbnailHeight"
                                        :element-width="thumbnailWidth"
                                        :grid="grid"
                                        :index="index"
                                        :layers_mapped="layers_mapped"
                                        :screens-data="screensData"
                                        :size-ratio="thumbnailSizeRatio"
                                        :thumbnail-size-not-small="thumbnailSizeNotSmall"
                                        :loading-deletion-state="loadingDeletionStateElementId >= 0"
                                        :loading-deletion-state-element-id="loadingDeletionStateElementId"
                                        @convertLink="convertLink(index)"
                                        @createCopy="copyElement(index)"
                                        @redirectTo="redirectToElement(index)"
                                        @softDelete="softDeleteElement(index)"
                                        @unDelete="unDeleteElement(index)" />
                                </provider-publish-table>
                            </div>
                        </template>
                    </draggable>
                    </transition>
                </div>
            </div>
            <publish-footer
                :deletedElements="deletedElements"
                :elements="visibleElements"
                :showDeleted="showDeleted"
                @input="handleChangeShowDeleted"
            />
        </v-card>

        <transmission nudge/>
        <show-media-modal/>
    </div>
</template>

<script>
import draggable from 'vuedraggable';
import debounce from 'debounce';
import datepicker from '../../common/datepicker.vue';
import transmission from './transmission.vue';
import showMediaModal from '../mediapool/show-media-modal.vue';
import publishElement from './publishElement.vue';
import publishFilter from './publishFilter.vue';
import { isBright } from '../../../utility'
import elementState from './elementstate.vue'
import PreviewSizeChangeButton from './button/preview-size-change-button.vue'
import toaster from "../../common/toaster";
import SquareButton from '../common/squareButton.vue'
import { dateUSToDateISO } from '../../../../utils/date'
import {adaptPublishLayer, checkElementSatisfiesFilters, getLayerIdLocalStorageKey} from '../../../../utils/publish'
import ProviderPublishTable from '../../common/providers/providerPublishTable.vue'
import PublishFooter from "./publish-footer.vue";
import LoadingPlaceholder from "../common/loadingPlaceholder.vue";
import VTooltipWrapper from "../../common/v-tooltip-wrapper.vue";
import PublishLayerSelect from "./publish-layer-select.vue";
import PublishPublishButton from "./button/publish-publish-button.vue";
import {calculateOrder} from "./publishUtils";
import PublishApproveAllButton from "./button/publish-approve-all-button.vue";
import PublishPreviewButton from "./button/publish-preview-button.vue";
import PublishReloadButton from "./button/publish-reload-button.vue";
import ApprovalSystem from "./ApprovalSystem";

export default {
    components: {
        PublishReloadButton,
        PublishPreviewButton,
        PublishApproveAllButton,
        PublishPublishButton,
        PublishLayerSelect,
        VTooltipWrapper,
        LoadingPlaceholder,
        PublishFooter,
        ProviderPublishTable,
        SquareButton,
        PreviewSizeChangeButton,
        elementState,
        draggable,
        datepicker,
        transmission,
        showMediaModal,
        publishElement,
        publishFilter,
    },
    props: {
        value: Number,
        /** @type {ScreensData} */
        screensData: Object,
        /** @type {PublishLayer_Active[]} */
        layers: Array,
        /** @type {PublishLayer_All[]} */
        all_layer_layers: Array,
        /** @type {'1' | undefined} */
        editPrivilege: String,
        /** @type {'1' | undefined} */
        deletePrivilege: String,
        /** @type {'1' | undefined} */
        approvePrivilege: String,
        grid: String,
        initLayerId: Number,
        initElementIndex: Number,
        creator_errors: Array,
        publishingDisabled: Boolean
    },
    data: function () {
        return {
            t_loading: Lang.langGet("Loading..."),
            t_load_failed: Lang.langGet("Failed to load layer data!"),
            t_layer_drop_here: Lang.langGet("Use Drag & Drop to add elements from the box on the left to this layer."),
            t_no_layers_available: Lang.langGet("Attention, there are no layers available!"),
            t_layer: Lang.langChoice("Layer|Layers", 1),
            t_link_convert_failed: Lang.langGet("Failed to convert the link to a local copy!"),
            t_not_enough_privileges: Lang.langGet("Not enough privileges to complete this action."),
            t_reject_success: Lang.langGet("All users that have access to this element will be notified per mail of this rejection."),
            t_reject_failed: Lang.langGet("You are not allowed to reject this element (missing screens / screengroups)"),
            t_reject_error: Lang.langGet("The notification about this rejection could not be sent!"),
            t_mark_delete_failed: Lang.langGet("Failed to mark layer element for deletion."),
            t_unmark_delete_failed: Lang.langGet("Failed to unmark layer element for deletion."),
            t_published: Lang.langGet("The layer will be published in a few moments."),
            t_update_failed: Lang.langGet("Failed to update layer elements."),
            t_reorder_failed: Lang.langGet("Failed to reorder layer elements."),
            t_outdated: Lang.langGet("Layer elements outdated. Please reload the layer."),
            t_copy_fail: Lang.langGet("Failed to copy element."),
            t_publish_limited_description: Lang.langGet("You have limited your view through filters. You only publish to the POV-Clients, which have the following screens/screen groups:"),
            t_publish_common_description: Lang.langGet("You only publish to the POV-Clients you have access to."),
            t_publish_all_screens: Lang.langGet('You will publish content to all accessible screens'),
            t_incomplete: Lang.langGet("Project Settings incomplete."),
            t_editor_layout: Lang.langGet("Editor Layout"),

            showDeleted: JSON.parse(localStorage.getItem('publishEditorShowDeleted')),
            activeLayerIndex: 0,
            loading: true,
            saving: false,
            publishing: false,
            preserveDateFilters: false,
            filter: {screen: '', screengroup: '', validFrom: '', validTo: ''},
            /** @type {PublishLayer} */
            active_layer: {fullname: '', elements: [], width: 0, height: 0, id: 0},
            dragging: false,
            positioned_scroll: true,
            /** @type {PublishLayer[]} */
            inner_layers: [],
            listBoxMinHeight: 0,
            editorHeight: 0,
            activeElementIndex: 0,
            startDragActiveElementId: 0,
            startDragDraggedElementId: 0,
            startDragDraggedElement: 0,
            reorderingProcess: false,
            newActiveElementIndex: -1,
            layoutInfo: [],
            windowInfo: [],

            taskQueue: [],
            processing: false,
            preventDrag: false,
            /** @type {'small' | 'medium' | 'large'}*/
            lineSize: 'small',

            editorScrollPos: 0,
            changingLayer: false,

            removedElementIds: {},

            loadingDeletionStateElementId: -1,
        }
    },
    computed: {
        deletedElements() {
            return this.active_layer.elements
                .filter((element, index) => this.isElementDeleted(element, index));
        },
        visibleElements() {
            const elements = this.active_layer.elements
                .filter((element, index) => this.isElementVisible(element, index));

            EaseEventBus.fire('updateVisibleElements', elements);
            return elements;
        },
        publishButtonColor() {
            if (this.publishingDisabled) return 'secondary';
            if (!this.inner_layers.length) return 'secondary';

            const everyElementSynced = this.active_layer.elements.every(element => element.state === 'synced');
            if (everyElementSynced) return 'secondary';

            return 'primary';
        },
        editorMessage() {
            if (this.loading) return this.t_loading;
            if (this.publishingDisabled) return this.t_incomplete;
            if (this.inner_layers.length) return this.t_layer_drop_here;
            return this.t_no_layers_available;
        },
        displayedElements() {
            if (this.showDeleted) return this.active_layer.elements;

            return this.active_layer.elements
                .filter(element => element.state !== 'tobedeleted' && element.state !== 'deleted' && element.state !== 'fulldelete');
        },
        localStorageKeyLineSize() {
            return `publish-line-size.${Laravel.activeUser}.${Laravel.activeProject}`
        },
        projectSecondaryColorIsDark: function () {
            return isBright(Laravel.secondary_color) === 'dark';
        },
        layerSelectOptions() {
            return this.inner_layers.map((layer, index) => ({...layer, value: index}));
        },
        publishScreenScreengroup() {
            const defaultScreengroupSelected = +(this.filter.screengroup) === this.screensData.default_screengroup;
            const hasNoScreenSelected = _.size(this.filter.screen) === 0;
            const hasNoScreengroupSelected = _.size(this.filter.screengroup) === 0;

            if (defaultScreengroupSelected && hasNoScreenSelected) return '';
            if (hasNoScreenSelected && hasNoScreengroupSelected) return '';

            let htmlString = '';

            if (_.size(this.filter.screen) > 0) {
                let tmp = _.filter(this.screensData.screens, (o) => {
                    return o.value === this.filter.screen;
                });

                if (_.size(tmp) > 0) {
                    htmlString = tmp[0].text;
                }
            } else {
                if (_.size(this.filter.screengroup) > 0) {
                    if (_.size(htmlString) > 0) {
                        htmlString += ', ';
                    }

                    let tmp = _.filter(this.screensData.screengroups, (o) => {
                        return o.value === this.filter.screengroup;
                    });

                    if (_.size(tmp) > 0) {
                        htmlString += tmp[0].text;
                    }
                }
            }

            return htmlString;
        },
        publishText() {
            const defaultScreengroupSelected = +(this.filter.screengroup) === this.screensData.default_screengroup;
            const hasScreensSelected = _.size(this.filter.screen) > 0;
            const hasScreengroupsSelected = _.size(this.filter.screengroup) > 0;

            if (defaultScreengroupSelected && !hasScreensSelected) {
                return this.t_publish_all_screens;
            }

            if (hasScreengroupsSelected || hasScreensSelected) {
                return this.t_publish_limited_description;
            }

            return this.t_publish_common_description;
        },
        active_element_id() {
            if (this.active_layer && this.active_layer.elements[this.activeElementIndex] !== undefined) {
                return this.active_layer.elements[this.activeElementIndex].id;
            }
            return undefined;
        },
        layers_mapped() {
            const map = {}
            this.all_layer_layers.forEach(layer => {
                map[layer.value] = layer
            })
            return map
        },
        layerAspectRatio() {
            const ratio = this.active_layer.width / this.active_layer.height
            return isNaN(ratio) ? 1 : ratio
        },
        thumbnailHeight() {
            switch(this.lineSize) {
                default:
                case 'small':
                    return 60
                case 'medium':
                    return 95
                case 'large':
                    return 128
            }
        },
        thumbnailWidth() {
            return this.thumbnailHeight * this.layerAspectRatio
        },
        thumbnailSizeNotSmall() {
            return this.lineSize != 'small'
        },
        thumbnailSizeRatio() {
            const sizeRatio = this.thumbnailHeight / this.active_layer.height
            return isNaN(sizeRatio) ? 1 : sizeRatio
        },
        nextGridSize() {
            switch (this.grid) {
                default:
                case '2cols':
                    return '3cols'
                case '3cols':
                    return '2cols'
            }
        },
        gridSizeIcon() {
            switch (this.grid) {
                default:
                case '2cols':
                    return 'fas fa-ellipsis-stroke'
                case '3cols':
                    return 'fas fa-ellipsis-stroke-vertical'
            }
        },
        /** @type {Record<number, boolean>} */
        shownElementIndices() {
            return this.getShownElementIndices();
        },
        visibleElementIndices() {
            return this.getVisibleElementIndices();
        },
        deletedElementIndices() {
            return this.getShownElementIndices(true);
        },
        /** @returns {PublishTableLayer} */
        tableLayer() {
            return adaptPublishLayer(this.active_layer, this.activeLayerIndex, this.visibleElementIndices, () => null)
        },
        placeholderPositionStyle() {
            return this.editorScrollPos == 0 ?
                { position: 'absolute', top: '0' } :
                { position: 'sticky', top: '-6px', height: 'calc(100% + 6px)' }
        },
        activeLayerState() {
            if(!this.active_layer.elements?.length) return 'empty'
            return this.active_layer.elements.every(element => element.state == 'synced') ? 'synced' : 'tobesynced'
        },
        approvalSystem() {
            return new ApprovalSystem({
                approvePrivilege: this.approvePrivilege,
                onElementUpdate: this.updateElement,
                getElements: () => this.visibleElements,
                onStateUpdate: () => this.updateElementState
            });
        }
    },
    watch: {
        activeLayerIndex(val) {
            localStorage.setItem(getLayerIdLocalStorageKey(), this.inner_layers[val]?.id || null);
            EaseEventBus.fire("layerSelected", val);
        },
        active_layer: {
            handler: function () {
                EaseEventBus.fire('updateActiveLayer', this.active_layer);
            },
            deep: true
        },
        activeElementIndex(val) {
            this.$emit('input', val);
            if (val !== -1) {
                EaseEventBus.fire('expandSettings');
            }
        },
        value: function () {
            if (this.active_layer.elements[this.value] && this.value !== this.activeElementIndex) {
                this.activeElementIndex = this.value;
            }
        },
        grid: function () {
            this.computeEditorHeight();
        },
        taskQueue: {
            handler: function () {
                this.processQueue();
            },
            deep: true
        },
        lineSize(val) {
            localStorage.setItem(this.localStorageKeyLineSize, val);
        },
        inner_layers: {
            handler(val) {
                this.$emit('update:layers', val)
            },
            deep: true
        },
        activeLayerState(val) {
            if(this.changingLayer) return
            this.inner_layers[this.activeLayerIndex].state = val
        },
    },
    methods: {
        handleElementClick() {
            EaseEventBus.fire('expandSettings');
        },
        isElementDeleted(element, index) {
            const hasBeenRemoved = this.removedElementIds[element.id];
            const isDeleted = this.deletedElementIndices[index];

            return hasBeenRemoved || isDeleted;
        },
        isElementVisible(element, index) {
            const hasBeenRemoved = this.removedElementIds[element.id];
            const isShown = this.shownElementIndices[index];
            const isDeleted = this.deletedElementIndices[index];

            return !hasBeenRemoved && (isShown || (this.showDeleted && isDeleted));
        },
        getVisibleElementIndices() {
            const visibleElementIndicesMap = {}
            if (!this.active_layer.elements) return visibleElementIndicesMap;
            this.active_layer.elements.forEach((element, index) => {
                if (this.isElementVisible(element, index)) {
                    visibleElementIndicesMap[index] = true;
                }
            })
            return visibleElementIndicesMap;
        },
        getShownElementIndices(onlyDeleted = false) {
            const shownElementIndicesMap = {}
            if (!this.active_layer.elements) return shownElementIndicesMap;

            /** @type {AdaptedPublishFilters} */
            const adaptedFilters = {
                screen: this.filter?.screen || null,
                screengroup: this.filter?.screengroup || null,
                validFrom: this.filter?.validFrom ? new Date(dateUSToDateISO(this.filter.validFrom)) : null,
                validTo: this.filter?.validTo ? new Date(dateUSToDateISO(this.filter.validTo)) : null
            }
            const filtersNotEmpty = Object.values(adaptedFilters).some(filterValue => filterValue);

            const isElementShown = element => {
                const isElementVisible = element => !element.data?.hidden;
                const isElementNotDeleted = element => (element.state !== 'tobedeleted' && element.state !== 'deleted' && element.state !== 'fulldelete');
                const isElementDeleted = element => (element.state === 'tobedeleted' || element.state === 'deleted' || element.state === 'fulldelete' || element.state === 'unknown');

                const elementSatisfiesFilter = element => {
                    if (!filtersNotEmpty) return true;
                    return checkElementSatisfiesFilters(element.data, adaptedFilters, this.screensData.screengroupsToScreens);
                }

                if (onlyDeleted) {
                    return isElementVisible(element) && isElementDeleted(element) && elementSatisfiesFilter(element);
                }

                return isElementVisible(element) && isElementNotDeleted(element) && elementSatisfiesFilter(element);
            }

            this.active_layer.elements.forEach((element, index) => {
                if (isElementShown(element)) {
                    shownElementIndicesMap[index] = true;
                }
            })

            return shownElementIndicesMap;
        },
        handleChangeShowDeleted(newValue) {
            localStorage.setItem('publishEditorShowDeleted', newValue);
            this.showDeleted = newValue;

            if(!newValue && this.deletedElementIndices[this.activeElementIndex]) {
                this.activeElementIndex = -1;
            }
        },
        clearActiveLayer() {
            this.active_layer = {
                fullname: '',
                elements: [],
                width: 0,
                height: 0,
                id: 0
            };
        },
        changeLayer: function () {
            EaseEventBus.fire('expandSettings');
            if (this.activeElementIndex > 0) {
                this.activeElementIndex = 0;
            }
            this.changingLayer = true
            setTimeout(() => this.changingLayer = false, 300)
            this.loadingDeletionStateElementId = -1
            localStorage.setItem('publish-layer-' + Laravel.activeProject, this.activeLayerIndex);

            this.clearActiveLayer();
            this.loadLayer();
        },
        alternativeElementUpdate: function (data) {
            this.active_layer.elements[this.activeElementIndex].data.alternative_id = data.id;
            this.active_layer.elements[this.activeElementIndex].data.alternative_type = data.type;
            this.active_layer.elements[this.activeElementIndex].data.alternative_background_image = data.url;
            this.active_layer.elements[this.activeElementIndex].data.alternative_name = data.name;

            this.$forceUpdate();
        },
        /**
         * @param task {Array}
         * @returns {boolean}
         */
        isAlreadyInTaskQueue(task) {
            const check = JSON.stringify(task);
            return this.taskQueue.filter((el) => {return JSON.stringify(el) === check}).length !== 0
        },
        /**
         * @param task {Array}
         */
        addTaskToQueue(task) {
            if(!this.isAlreadyInTaskQueue(task)) {
                this.taskQueue.push(task);
            }
        },
        loadLayer() {
            this.addTaskToQueue(['loadLayerDirectly']);
        },
        loadLayerDirectly(layerNr) {
            this.publishing = false;

            if (this.newActiveElementIndex === -1) {
                this.newActiveElementIndex = this.activeElementIndex;
            }
            this.activeElementIndex = -1;

            if (!this.inner_layers[this.activeLayerIndex]) {
                EaseEventBus.fire('taskDone');
                this.loading = false;
                return;
            }

            this.loading = true;
            EaseEventBus.fire('startLoadingLayer');

            const layer = this.inner_layers[this.activeLayerIndex];

            const data = {
                project: Laravel.activeProject,
                layer: layerNr || layer.value, // Nr of layer not Id or index
                filter: this.filter
            };


            axios.post('/content/publish/get', data).then((response) => {
                if (layer.is_calendar) this.$emit('change-grid', '2cols');

                this.loading = false;
                /** @type {PublishLayer} */
                const publishLayer = response.data.data
                publishLayer.name = publishLayer.name.replaceAmpGtLt()
                publishLayer.fullname = publishLayer.fullname.replaceAmpGtLt()
                publishLayer.fullname_size = publishLayer.fullname_size.replaceAmpGtLt()

                if (Array.isArray(publishLayer.elements)) {
                    publishLayer.elements.forEach((el) => {
                        if (!Object.prototype.hasOwnProperty.call(el.data, 'literal_times')) {
                            el.data.literal_times = false;
                        }
                    });
                }

                this.active_layer = _.extend({}, this.active_layer, publishLayer);
                this.inner_layers[this.activeLayerIndex].elementCount = this.active_layer.elements.length;

                this.$nextTick(() => {
                    EaseEventBus.fire('loadedLayer');
                    EaseEventBus.fire('reinitializeDragula');
                    this.processWindowSize();

                    if (this.newActiveElementIndex >= 0) {
                        this.activeElementIndex = this.newActiveElementIndex;
                        this.newActiveElementIndex = -1;
                        EaseEventBus.fire('publishScrollToElement', this.activeElementIndex);
                    }
                });
            }).catch((error) => {
                this.loading = false;
                if (error.response?.status === 401) {
                    window.location = '/login';
                }

                toaster.error(this.t_load_failed);
            }).finally(() => {
                EaseEventBus.fire('taskDone');
            });
        },
        publish(id = this.active_layer.id) {
            if (this.publishing) return;
            this.publishing = true;

            axios.post('/content/publish/publish', {
                id: id,
                project: Laravel.activeProject,
                user: Laravel.activeUser,
                order: calculateOrder(this.active_layer.elements),
                visible: this.visibleElements,
                limit_screen: this.filter.screen,
                limit_screengroup: this.filter.screengroup
            }).then(() => {
                toaster.success(this.t_published);

                this.updateActiveElementsPublishTS();

                this.$nextTick(() => {
                    this.publishing = false;
                });
            }).catch((error) => {
                if (error.response?.status === 401) {
                    window.location = '/login';
                }

                if (error.response?.data?.data?.outdated) {
                    toaster.error(this.t_outdated);
                }

                this.publishing = false;
            });
        },
        getElementState(element) {
            if (element.state === 'fulldelete') return 'fulldelete'; //fulldelete state shall be immutable
            let state = (element.deleted_at === null) ? 'synced' : 'deleted';

            if (element.published_at !== null && element.deleted_at !== null) return 'deleted';

            if (
                (element.data.screens.length === 0 && element.data.screengroups.length === 0) ||
                element.data.approved === false
            ) {
                state = 'error';
            } else {
                if (element.published_at !== null && element.published_at !== 'outdated') {
                    if (element.published_at !== 'updated') {
                        if (element.published_at < element.updated_at) {
                            state = 'tobe' + state;
                        } else if (element.published_at > element.updated_at) {
                            state = 'part' + state;
                        }
                    } else {
                        state = 'updating';
                    }
                } else {
                    state = 'tobesynced';
                }
            }

            return state;
        },
        isOverlayActive: function () {
            if (this.loading) return true;
            if (!this.inner_layers.length) return true;
            if (this.active_layer.elements.length === 0) return true;
            if (this.dragging) return true;

            const elementsWithoutDeleted = this.active_layer.elements
                .filter(element => element.state !== 'tobedeleted' && element.state !== 'deleted');
            if (!this.showDeleted && elementsWithoutDeleted.length === 0) return true;

            return (this.inner_layers[this.activeLayerIndex].state === 'empty');
        },
        updateElementState(visibleIndex) {
            const element = this.visibleElements[visibleIndex];

            if (element) {
                element.state = this.getElementState(element);
            }
        },
        updateActiveElementsPublishTS: function () {
            this.removedElementIds = {};
            $.each(this.active_layer.elements, (key, element) => {
                if (element.state !== 'error') {
                    if (this.shownElementIndices[key]) {
                        const vIndex = this.visibleElements.findIndex(vElement => vElement.id === element.id);
                        this.updateElement(vIndex, element.state.includes('deleted') ? 'deleted' : 'updated');
                    } else if(element.state !== 'error' && element.deleted_at) {
                        this.taskQueue.push(['removeElementFromList', element.id]);
                    }
                }
            });

            this.taskQueue.push(['deleteRemovedElements']);
        },
        startDrag: function (event) {
            if(this.processing || this.taskQueue.length > 0) {
                event.preventDefault();
                this.preventDrag = true;
                return;
            }

            this.reorderingProcess = true;

            this.startDragActiveElementId = this.active_element_id;
            this.startDragDraggedElementId = parseInt(event.item.firstElementChild.getAttribute('data-id'));
            this.startDragDraggedElement = _.map(this.active_layer.elements, 'id').indexOf(this.startDragDraggedElementId);
        },
        handleDragChange(newElements) {
            const indexOfNewElement = newElements.findIndex(element => element.id === this.startDragDraggedElementId);
            const newElement = newElements.find(element => element.id === this.startDragDraggedElementId);

            // remove moved element in original array to position it correctly
            this.active_layer.elements = this.active_layer.elements.filter(element => element.id !== this.startDragDraggedElementId);

            // moved to start
            if (indexOfNewElement === 0) {
                this.active_layer.elements.unshift(newElement);
                return;
            }

            const previousElement = newElements[indexOfNewElement - 1];

            const previousElementOriginalIndex = this.active_layer.elements.findIndex(element => element.id === previousElement.id);
            this.active_layer.elements.splice(previousElementOriginalIndex + 1, 0, newElement);
        },
        moveDrag() {
            return !this.preventDrag;
        },
        endDrag: function (event) {
            if(this.preventDrag) {
                event.preventDefault();
                this.preventDrag = false;
                return;
            }

            this.taskQueue.push(['reorder']);
        },
        reorder: function () {
            const activeLayer = this.active_layer;
            const elements = activeLayer.elements;
            this.activeElementIndex = -1;
            const newElementIndex = _.map(elements, 'id').indexOf(this.startDragActiveElementId);
            const currentElementIndex = _.map(elements, 'id').indexOf(this.startDragDraggedElementId);

            axios.post('/content/publish/reorder', {
                id: activeLayer.id,
                project_id: activeLayer.project_id,
                elements: _.map(elements, 'id'),
                movedElementId: this.startDragDraggedElementId,
            }).then(() => {
                this.activeElementIndex =  (newElementIndex !== -1) ? newElementIndex : currentElementIndex;
                const currentElement = elements[currentElementIndex];

                if (currentElementIndex !== this.startDragDraggedElement && currentElement) {
                    const {published_at, state} = currentElement;

                    if (published_at && state !== 'deleted' && state !== 'tobedeleted') {
                        currentElement.published_at = "outdated";
                    }
                }

                const vindex = this.visibleElements.findIndex(velement => velement.id === currentElement.id);
                this.updateElementState(vindex);
            }).catch((error) => {
                if (error.response?.status === 401) {
                    window.location = '/login';
                }

                if (error.response?.data?.data?.outdated) {
                    toaster.error(this.t_outdated);
                } else {
                    toaster.error(this.t_reorder_failed);
                }
            }).finally(() => {
                this.reorderingProcess = false;
                EaseEventBus.fire('taskDone');
                this.reloadLayer();
            });
        },
        processQueue: debounce(function () {
            if(!this.processing) {
                if(this.taskQueue.length > 0) {
                    this.processing = true;
                    let task = this.taskQueue.splice(0, 1);
                    this[task[0][0]](task[0][1]);
                } else {
                    this.loading = false;
                }
            }
        }, 1),
        updateElement: function (visibleIndex, newPublishedAt = 'outdated') {
            if (!this.creator_errors[visibleIndex]?.errors?.length) {
                this.visibleElements[visibleIndex].published_at = newPublishedAt;
                this.taskQueue.push(['updateElementRequest', this.visibleElements[visibleIndex].id]);
                this.updateElementState(visibleIndex);
            }
        },
        deleteElementById(id) {
            const index = this.active_layer.elements.findIndex(element => element.id === id);
            this.active_layer.elements.splice(index, 1);
        },
        replaceElementById(id, newElement) {
            const index = this.active_layer.elements.findIndex(element => element.id === id);
            Object.assign(this.active_layer.elements[index], newElement);
        },
        updateElementRequest(currentId) {
            if (this.saving || this.loading) {
                this.addTaskToQueue(['updateElementRequest', currentId]);
                return;
            }

            const currentElement = this.active_layer.elements.find(element => element.id === currentId);
            if (!currentElement) return;

            this.saving = true;

            const updateData = {
                id: currentId,
                project_id: Laravel.activeProject,
                data: currentElement.data,
                change_count: currentElement.change_count,
                published_at: currentElement.published_at,
                limit: {screen: this.filter.screen, group: this.filter.screengroup},
            }

            const url = '/content/publish/update';

            axios.post(url, updateData).then(result => {
                if (!_.isObject(result.data)) return;

                if (result.data.state === 'deleted') {
                    this.deleteElementById(currentId);
                    this.reloadLayer();
                } else {
                    this.replaceElementById(currentId, result.data);
                }
            }).catch((error) => {
                if (error.response?.status === 401) {
                    window.location = '/login';
                }

                if (error.response?.data?.data?.outdated) {
                    toaster.error(this.t_outdated);
                } else {
                    toaster.error(this.t_update_failed);
                }
            }).finally(() => {
                this.saving = false;
                EaseEventBus.fire('taskDone');

            });
        },
        setAllElementsVisibility: function () {
            /** @type {AdaptedPublishFilters} */
            const adaptedFilters = {
                screen: this.filter?.screen || null,
                screengroup: this.filter?.screengroup || null,
                validFrom: this.filter?.validFrom ? new Date(dateUSToDateISO(this.filter.validFrom)) : null,
                validTo: this.filter?.validTo ? new Date(dateUSToDateISO(this.filter.validTo)) : null
            }
            const filtersEmpty = Object.values(adaptedFilters).every(filterValue => !filterValue)
            this.active_layer.elements?.forEach(element => {
                /* checkElementSatisfiesFilters result is inverted here, because the logic is inverted.
                *  The function returns true if the element should show, which in case of filter_hidden means false (not hidden) */
                element.data.filter_hidden = element.data.hidden || (filtersEmpty ? false : !checkElementSatisfiesFilters(element.data, adaptedFilters, this.screensData.screengroupsToScreens, !!this.editPrivilege))
            })
        },
        convertLink: function (index) {
            const element = this.visibleElements[index];
            // borrowing convert function from campaign
            axios.post('/content/' + element.data.type+ '/convert', {
                id: element.data.id,
                project: Laravel.activeProject,
                type: element.data.type
            }).then(() => {
                element.data.state = 'copy';
            }).catch((error) => {
                if (error.response?.status === 401) {
                    window.location = '/login';
                } else if (error.response?.status === 403) {
                    toaster.error(this.t_not_enough_privileges);
                } else if (error.response?.status === 422) {
                    let errorString = '';

                    for (let key in error.response.data) {
                        errorString = errorString + error.response.data[key][0] + ' ';
                    }

                    toaster.error(errorString);
                } else {
                    toaster.error(this.t_link_convert_failed);
                }
            });
        },
        editElement(element) {
            EaseEventBus.fire('showModal', 'edit-element-' + element);
        },
        /**
         * @param element {PublishElement}
         * @param date {Date}
         */
        setDeletedDate(element, date = new Date()) {
            element.deleted_at = date.toISOString();
        },
        softDeleteElement(elementIndex) {
            const currentElement = this.visibleElements[elementIndex];
            // Its called Id but it refers to the index?!
            this.loadingDeletionStateElementId = elementIndex;

            axios.post('/content/publish/delete', {
                project: Laravel.activeProject,
                element_id: currentElement.id,
                layer_id: currentElement.layer_id,
            }).then(() => {
                const elementIsNotPublished = !currentElement.published_at;

                if (elementIsNotPublished) this.deleteElementById(currentElement.id);
                else this.setDeletedDate(currentElement);

            }).catch(() => {
                toaster.error(this.t_mark_delete_failed);
            }).finally(() => {
                this.loadingDeletionStateElementId = -1;
                if (!this.showDeleted) {
                    this.activeElementIndex = -1;
                }
            });
        },
        unDeleteElement: function (elementIndex) {
            const currentElement = this.visibleElements[elementIndex];
            // Its called Id but it refers to the index?!
            this.loadingDeletionStateElementId = elementIndex;

            axios.post('/content/publish/undelete', {
                project: Laravel.activeProject,
                element_id: currentElement.id,
                layer_id: currentElement.layer_id
            }).then(() => {
                currentElement.deleted_at = null;
                this.reloadLayer();
            }).catch(() => {
                toaster.error(this.t_unmark_delete_failed);
            }).finally(() => this.loadingDeletionStateElementId = -1);
        },
        removeElementFromList(elementId) {
            this.removedElementIds[elementId] = true;
            EaseEventBus.fire('taskDone');
        },
        deleteRemovedElements() {
            this.active_layer.elements = this.active_layer.elements.filter(element => !this.removedElementIds[element.id]);
            this.removedElementIds = {};
            EaseEventBus.fire('taskDone');
        },
        rejectElement(index) {
            if (!this.approvalSystem.checkForApprovePrivilegeAndWarn()) {
                return;
            }

            const currentElement = this.visibleElements[index];

            axios.post('/content/publish/reject', {
                project: Laravel.activeProject,
                id: currentElement.id,
            }).then(() => {
                toaster.success(this.t_reject_success);
                this.approvalSystem.disapprove(index);
            }).catch((error) => {
                if (error.response?.status === 401) {
                    window.location = '/login';
                }

                toaster.error(this.t_reject_error);
            });
        },
        redirectToElement(index) {
            this.saving = true;
            const element = this.visibleElements[index];

            this.$emit('redirect', element);
        },
        copyElement: function (index) {
            axios.post('/content/publish/insert', {
                project: Laravel.activeProject,
                layer_id: this.active_layer.id,
                element: this.visibleElements[index].data
            }).then(() => {
                this.reloadLayer('insert');
            }).catch((error) => {
                toaster.error(this.t_copy_fail);
                EaseEventBus.fire('finishedLoadingLayer');
            });
        },
        processWindowSize() {
            let w = window.innerWidth;
            let h = window.innerHeight;

            //30: l+r padding layer row, 40: l+r padding wrapper content
            let maxWidth = (w - 30 - 40) / 12;

            this.computedMaxWidth = maxWidth;
            this.computedMaxHeight = maxWidth * this.active_layer.height / this.active_layer.width;

            if (this.$refs.listBox && this.$refs.listContent) {
                let top = this.$refs.listBox.getBoundingClientRect().top;
                let wrapperPadding = 20;
                let publishPadding = 15;
                let iboxMargin = 15;
                let footerHeight = 39;
                let listBoxMinHeight = Math.max(h - top - footerHeight - iboxMargin * 2 - publishPadding - wrapperPadding, 150);
                this.listBoxMinHeight = listBoxMinHeight;
            }

            this.computeEditorHeight();
        },
        computeEditorHeight: function () {
            const defaultHeight = 200;
            const footerHeight = 30;
            const paddingFooter = 15;
            let h = 0;

            const layoutInfo = this.layoutInfo;
            const windowInfo = this.windowInfo;
            if (layoutInfo && windowInfo && this.$refs.listBox) {
                let top = this.$refs.listBox.getBoundingClientRect().top;
                h = Math.max(defaultHeight, window.innerHeight - top - layoutInfo.footerHeight - layoutInfo.wrapperTBpadding);

                if (isNaN(h)) {
                    h = Math.max(defaultHeight, window.innerHeight - top - footerHeight - paddingFooter);
                }
            } else {
                h = Math.max(defaultHeight, this.editorHeight);
            }
            this.editorHeight = h - footerHeight;
        },
        onFiltersChanged(filters) {
            this.filter = filters;
        },
        reloadLayer(origin = '') {
            if (origin === 'insert') {
                this.newActiveElementIndex = this.visibleElements.length;
            }
            this.loadLayer();
        },
        handleEditorScroll (e) {
            this.editorScrollPos = e.target.scrollTop
        },
        scrollToEnd() {
            if (this.$refs.listBox) {
                this.$refs.listBox.scrollTo(0, this.$refs.listBox.scrollHeight);
            }
        }
    },
    created() {
        this.inner_layers = JSON.parse(JSON.stringify(this.layers));
        this.lineSize = localStorage.getItem(this.localStorageKeyLineSize) ?? 'medium';
    },
    mounted() {
        this.activeElementIndex = this.value;
        const layerId = this.initLayerId < 0 ? JSON.parse(localStorage.getItem(getLayerIdLocalStorageKey())) : this.initLayerId

        if (layerId == null) {
            this.activeLayerIndex = 0;
        } else {
            this.activeLayerIndex = this.inner_layers.findIndex(layer => layer.id == layerId)
            if (this.activeLayerIndex < 0) this.activeLayerIndex = 0
        }

        localStorage.setItem('publish-layer-' + Laravel.activeProject, this.activeLayerIndex);
        localStorage.setItem(getLayerIdLocalStorageKey(), layerId);

        this.newActiveElementIndex = this.initElementIndex

        const layer = this.inner_layers.find(layer => layer.id === layerId);
        const layerNr = layer ? layer.nr : 0;
        this.loadLayerDirectly(layerNr);

        EaseEventBus.listen('dragging', (dragging) => {
            this.dragging = dragging;
            if(dragging === true) {
                EaseEventBus.fire('startLoadingLayer');
            }
        });

        EaseEventBus.listen('windowInfo', (data) => {
            this.windowInfo = _.extend({}, this.windowInfo, data);
        });
        EaseEventBus.listen('layoutInfo', (data) => {
            this.layoutInfo = _.extend({}, this.layoutInfo, data);
        });
        EaseEventBus.listen('rejectSelectedElement', (index) => {
            this.rejectElement(index);
        });
        EaseEventBus.listen('approveSelectedElement', (index) => {
            this.approvalSystem.approve(index);
        });
        EaseEventBus.listen('elementChanged', element => {
            this.updateElementRequest(element.id);
        });
        EaseEventBus.listen('alternativeElementUpdate', (data) => {
            this.alternativeElementUpdate(data);
        });
        EaseEventBus.listen('publishScrollToElement', (index) => {
            this.$nextTick(() => { this.scrollToEnd() });
        });
        EaseEventBus.listen('changeLayer', (id) => {
            this.activeLayerIndex = id;
            this.changeLayer();
        });
        EaseEventBus.listen('updateElementState', (id) => {
            this.updateElementState(id);
        });

        EaseEventBus.listen('publishFilterMounted', () => {
            this.preserveDateFilters = true;
        });

        EaseEventBus.listen('taskDone', () => {
            this.processing = false;
            this.processQueue();
        });

        EaseEventBus.listen('changeActiveElementIndex', (index) => {
            this.activeElementIndex = index;
        });

        this.$nextTick(() => {
            EaseEventBus.fire('windowInfo', this.windowInfo); // to let all elements know of the layoutInfo
            EaseEventBus.fire('layoutInfo', this.layoutInfo); // to let all elements know of the layoutInfo
            window.addEventListener('resize', this.processWindowSize);
            window.addEventListener('scroll', this.processWindowSize);
            this.processWindowSize();
        });
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.processWindowSize);
        window.removeEventListener('scroll', this.processWindowSize);
        EaseEventBus.destroy('elementChanged');
        EaseEventBus.destroy('changeLayer');
    }
}
</script>

<style scoped>

.v-btn {
    padding: 0 5px 0 10px !important;
    font-size: 0.83rem !important;
    .v-btn__content .v-icon--left {
        margin-right: 5px !important;
    }
}

.publish-editor-controls-container {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    gap: 6px;
}

.publish-editor-header {
    z-index: 1;
    position: relative;
    padding-inline: 12px;
    padding-block: 12px;
    display: flex;
    flex-direction: column;
    gap: 8px;
}

#select-active_dropdown {
    display: flex;
    flex-direction: row;
    align-content: center;
    flex-grow: 1;
    gap: 6px;
}

#select-active_dropdown .publish-select-layer-dropdown {
    min-width: 150px;
    width: 150px;
    flex-grow: 1;
}

#select-active_dropdown .publish-select-layer-buttons {
    display: flex;
    align-content: center;
    flex-wrap: wrap;
    gap: 6px;
}

.publish-element-container:nth-child(odd) {

    /*noinspection CssUnusedSymbol*/
    .publish-element {
        --publish-row-background-color: #f1f2f3;
        --publish-row-title-background-color: #f1f2f3;

        &:hover {
            --publish-row-title-background-color: #ffffff;
        }

        /*noinspection CssUnusedSymbol*/
        &.active {
            --row-active-background-color: #d0e6ff;
            --publish-row-title-background-color: #ffffff;
        }
    }
}
</style>
